import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import { InputField } from 'components/shared/input'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { Alert, Button, Table } from 'react-bootstrap'
import { CSSProperties } from 'styled-components'
import { useCancelExamApi, useSearchUserApi } from 'api/user_search'
import { SampleViewModel } from 'view_models/sample_view_model'
import { toDateText } from 'utils/util'
import { useDownloadUserExportApi } from 'api/user_export'
import { formatRequestFormCode } from 'utils/formatRequestFormCode'

type Props = {
    history: H.History
}

export type UserSearchForm = {
    requestCode?: string
    sampleCode?: string
    orderName?: string
    orderNameKana?: string
    orderTel?: string
    orderEmail?: string
    patientFamilyName?: string
    patientGivenName?: string
    patientFamilyNameKana?: string
    patientGivenNameKana?: string
    patientTel?: string
    patientEmail?: string
}

const UserSearchPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const form = useForm<UserSearchForm>({})
    const api = useSearchUserApi(form)
    const dlApi = useDownloadUserExportApi()
    const cancelExamApi = useCancelExamApi()

    if (!globalState.user.isSuperuser) {
        globalState.setNotificationMessage({ body: '権限がありません', colorType: 'danger' })
        props.history.replace('dashboards')
    }

    const width = (w: number): CSSProperties => {
        return { width: w, minWidth: w, textAlign: 'center' }
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess() && api.response.results.length === 0) {
            globalState.setNotificationMessage({ body: '結果がありません', colorType: 'warning' })
        }
    }, [api.loading])

    useEffect(() => {
        globalState.setLoading(cancelExamApi.loading)
        if (cancelExamApi.isSuccess()) {
            globalState.setNotificationMessage({ body: '取り消しました', colorType: 'success' })
            api.execute()
        }
    }, [cancelExamApi.loading])

    useEffect(() => {
        if (form.object.requestCode) {
            form.set({ requestCode: formatRequestFormCode(form.object.requestCode) })
        }
    }, [form.object.requestCode])

    return (
        <>
            <div style={{ width: 1000 }}>
                <div>
                    <Button
                        onClick={() => {
                            dlApi.execute()
                        }}
                    >
                        ユーザーデータDL
                    </Button>
                </div>
                <Flex>
                    <InputField form={form} attr="requestCode" label="依頼書ID(バーコード)" placeholder="7桁の英数字または12桁の数字" />
                    <InputField form={form} attr="sampleCode" label="検体ID" placeholder="S_xxxxxxx" onlyHankaku />
                </Flex>
                <Flex>
                    <div style={{ padding: 16, backgroundColor: '#f001' }}>
                        <div>購入者情報</div>
                        <div>
                            <Flex>
                                <InputField form={form} attr="orderName" label="氏名" />
                                <InputField form={form} attr="orderNameKana" label="カナ" />
                            </Flex>
                            <Flex>
                                <InputField form={form} attr="orderTel" label="電話番号" onlyHankaku />
                                <InputField form={form} attr="orderEmail" label="メールアドレス" type="email" onlyHankaku />
                            </Flex>
                        </div>
                    </div>
                    <div style={{ padding: 16, backgroundColor: '#00f1' }}>
                        <div>受検者情報(Web問診をした人)</div>
                        <div>
                            <Flex>
                                <InputField form={form} attr="patientFamilyName" label="姓" />
                                <InputField form={form} attr="patientGivenName" label="名" />
                                <InputField form={form} attr="patientFamilyNameKana" label="セイ" />
                                <InputField form={form} attr="patientGivenNameKana" label="メイ" />
                            </Flex>
                            <Flex>
                                <InputField form={form} attr="patientTel" label="電話番号" onlyHankaku />
                                <InputField form={form} attr="patientEmail" label="メールアドレス" type="email" onlyHankaku />
                            </Flex>
                        </div>
                    </div>
                </Flex>
                <Flex style={{ marginTop: 16 }}>
                    <Button
                        style={{ width: '100%' }}
                        onClick={() => {
                            api.execute()
                        }}
                    >
                        検索
                    </Button>
                    <Button
                        style={{ width: 160 }}
                        variant="secondary"
                        onClick={() => {
                            form.set({})
                        }}
                    >
                        リセット
                    </Button>
                </Flex>
            </div>
            {api.response.results.length > 0 && (
                <div style={{ overflow: 'scroll', marginTop: 24 }}>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th rowSpan={2} style={width(160)}>
                                    依頼書ID
                                </th>
                                <th rowSpan={2} style={width(160)}>
                                    検体ID
                                </th>
                                <th colSpan={5} style={{ backgroundColor: '#f001' }}>
                                    購入者
                                </th>
                                <th colSpan={4} style={{ backgroundColor: '#00f1' }}>
                                    受検者
                                </th>
                                <th rowSpan={2} style={width(150)}>
                                    ステータス
                                </th>
                                <th rowSpan={2} style={width(120)}>
                                    申込日
                                </th>
                                <th rowSpan={2} style={width(120)}>
                                    検体受領日
                                </th>
                                <th rowSpan={2} style={width(120)}>
                                    結果票発送日
                                </th>
                                <th rowSpan={2} style={width(120)}>
                                    PcrRunID
                                </th>
                                <th rowSpan={2} style={width(80)}>
                                    結果票
                                </th>
                            </tr>
                            <tr>
                                <th style={width(150)}>購入者ID</th>
                                <th style={width(150)}>氏名</th>
                                <th style={width(150)}>カナ</th>
                                <th style={width(150)}>電話番号</th>
                                <th style={width(300)}>メールアドレス</th>
                                <th style={width(150)}>氏名</th>
                                <th style={width(150)}>カナ</th>
                                <th style={width(150)}>電話番号</th>
                                <th style={width(300)}>メールアドレス</th>
                                <th style={width(100)}>取り消し</th>
                            </tr>
                        </thead>
                        <tbody>
                            {api.response.results.map((rf) => {
                                const s = rf.sample
                                const p = s?.patient
                                const co = rf.customerOrder
                                return (
                                    <tr key={rf.id}>
                                        <td>{rf.code}</td>
                                        <td>{s?.code}</td>
                                        <td>{co?.customerCode}</td>
                                        <td>{co?.name}</td>
                                        <td>{co?.nameKana}</td>
                                        <td>{co?.tel}</td>
                                        <td>{co?.email}</td>
                                        <td>{p ? `${p.familyName} ${p.givenName}` : ''}</td>
                                        <td>{p ? `${p.familyNameKana} ${p.givenNameKana}` : ''}</td>
                                        <td>{p?.tel}</td>
                                        <td>{p?.email}</td>
                                        <td style={{ textAlign: 'center' }}>{s ? new SampleViewModel(s).statusView() : ''}</td>
                                        <td>{toDateText(co?.orderedDate)}</td>
                                        <td>{toDateText(s?.inspectionDate)}</td>
                                        <td>{toDateText(s?.sendDate)}</td>
                                        <td>
                                            {rf.pcrRunIds?.map((i) => (
                                                <a
                                                    key={rf.id + 'pcr_run' + i}
                                                    href={`pcr_run/${i}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ marginRight: 2 }}
                                                >
                                                    {i}
                                                </a>
                                            ))}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {s && (s.status === 'PRINT_REQUESTED' || s.status === 'SENT') ? (
                                                <a href={`send_histories/preview_results?sampleId=${s.id}`} target="_blank" rel="noreferrer">
                                                    開く
                                                </a>
                                            ) : (
                                                ''
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <Button
                                                variant="danger"
                                                onClick={() => {
                                                    globalState.showConfirm(
                                                        <>
                                                            <Alert variant="danger">この操作は取り消せません。慎重に行ってください</Alert>
                                                            <div>検体ID: {s?.code}</div>
                                                            <div>依頼書ID: {rf.code}</div>
                                                        </>,
                                                        () => {
                                                            cancelExamApi.execute(rf.id!)
                                                        },
                                                        { confirmWord: '取り消す' }
                                                    )
                                                }}
                                            >
                                                取消
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    )
}

export default withRouter(UserSearchPage)
